import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Center,
  Group,
  Loader,
  Modal,
  Stack,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconAlertTriangle,
  IconPencil,
  IconPlus,
  IconTags,
  IconTrash,
} from '@tabler/icons-react';
import { useEffect, useState, type FormEvent } from 'react';
import { PageHeader } from '@/components/page-header';
import { ApiError, ApiErrorCode, NetworkError } from '@/lib/api-error';
import {
  createInterest,
  deleteInterest,
  listInterests,
  updateInterest,
  type Interest,
  type InterestDraft,
} from '@/lib/interests';
import classes from './settings-page.module.css';

/** What the form is for: adding an interest, or editing the one it holds. */
type Subject = { mode: 'create' } | { mode: 'edit'; interest: Interest };

function describeError(error: unknown): string {
  if (error instanceof NetworkError) {
    return 'The API did not answer. It may not be running.';
  }
  if (error instanceof ApiError) return error.message;
  return 'Something went wrong.';
}

/**
 * Client-side checks, mirroring what the server enforces.
 *
 * A convenience so a typo does not cost a round trip — not a control. This
 * bundle is public and modifiable, so the API validates the same fields again
 * and is the only thing that decides what gets stored. The pattern is the one
 * in `api/src/interests/dto/`, not the two-or-three-letter language pattern the
 * other slug screens grew from.
 */
function validate(
  draft: InterestDraft,
): Partial<Record<keyof InterestDraft, string>> {
  const errors: Partial<Record<keyof InterestDraft, string>> = {};

  if (!draft.label.trim()) errors.label = 'Required.';

  const code = draft.code.trim();
  if (!code) {
    errors.code = 'Required.';
  } else if (!/^[a-zA-Z][a-zA-Z0-9-]*$/.test(code) || code.length < 2) {
    errors.code = 'A slug, such as food or city-life.';
  }

  return errors;
}

interface InterestFormProps {
  subject: Subject;
  onClose: () => void;
  onSaved: (interest: Interest, mode: Subject['mode']) => void;
}

/** One form for both adding and editing. See the reasons screen for why. */
function InterestForm({ subject, onClose, onSaved }: InterestFormProps) {
  const [draft, setDraft] = useState<InterestDraft>(() =>
    subject.mode === 'edit'
      ? { label: subject.interest.label, code: subject.interest.code }
      : { label: '', code: '' },
  );
  const [fieldErrors, setFieldErrors] = useState<
    Partial<Record<keyof InterestDraft, string>>
  >({});
  const [submitError, setSubmitError] = useState<unknown>(null);
  const [isSaving, setIsSaving] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();

    const errors = validate(draft);
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    const trimmed: InterestDraft = {
      label: draft.label.trim(),
      code: draft.code.trim(),
    };

    setIsSaving(true);
    setSubmitError(null);
    try {
      const saved =
        subject.mode === 'edit'
          ? await updateInterest(subject.interest.id, trimmed)
          : await createInterest(trimmed);
      onSaved(saved, subject.mode);
      onClose();
    } catch (caught) {
      setSubmitError(caught);
    } finally {
      setIsSaving(false);
    }
  }

  // A conflict is about one field and belongs beside it, not in a banner at
  // the top of a two-field form.
  const conflict =
    submitError instanceof ApiError && submitError.is(ApiErrorCode.CONFLICT)
      ? submitError.message
      : null;

  return (
    <form onSubmit={(event) => void submit(event)} noValidate>
      <Stack gap="md">
        {submitError !== null && conflict === null && (
          <Alert color="red" icon={<IconAlertTriangle size={18} />}>
            <Text size="sm">{describeError(submitError)}</Text>
          </Alert>
        )}

        <TextInput
          label="Interest"
          description="What this portal calls it. Nobody learning a language ever sees it."
          placeholder="Food and drink"
          required
          data-autofocus
          value={draft.label}
          error={fieldErrors.label}
          onChange={(event) =>
            setDraft({ ...draft, label: event.currentTarget.value })
          }
        />

        <TextInput
          label="Code"
          description="A stable slug. What somebody is interested in is recorded against it, so changing one orphans every answer already given."
          placeholder="food"
          required
          value={draft.code}
          error={fieldErrors.code ?? conflict ?? undefined}
          onChange={(event) =>
            setDraft({ ...draft, code: event.currentTarget.value })
          }
        />

        <Group justify="flex-end" gap="sm">
          <Button
            variant="subtle"
            color="gray"
            onClick={onClose}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button type="submit" loading={isSaving}>
            {subject.mode === 'edit' ? 'Save changes' : 'Add interest'}
          </Button>
        </Group>
      </Stack>
    </form>
  );
}

interface RemoveFormProps {
  interest: Interest;
  onClose: () => void;
  onRemoved: (interest: Interest) => void;
}

/** Confirmation before a delete, which also untags every headline. */
function RemoveForm({ interest, onClose, onRemoved }: RemoveFormProps) {
  const [error, setError] = useState<unknown>(null);
  const [isRemoving, setIsRemoving] = useState(false);

  async function confirm() {
    setIsRemoving(true);
    try {
      await deleteInterest(interest.id);
      onRemoved(interest);
      onClose();
    } catch (caught) {
      setError(caught);
    } finally {
      setIsRemoving(false);
    }
  }

  return (
    <Stack gap="md">
      {error !== null && (
        <Alert color="red" icon={<IconAlertTriangle size={18} />}>
          <Text size="sm">{describeError(error)}</Text>
        </Alert>
      )}

      <Text size="sm">
        {interest.label} is removed from every headline that carries it. What
        has already been recorded about people who picked those headlines is
        kept &mdash; it is written against the code rather than this row, so it
        survives.
      </Text>

      <Group justify="flex-end" gap="sm">
        <Button
          variant="subtle"
          color="gray"
          onClick={onClose}
          disabled={isRemoving}
        >
          Cancel
        </Button>
        <Button color="red" loading={isRemoving} onClick={() => void confirm()}>
          Remove
        </Button>
      </Group>
    </Stack>
  );
}

export function InterestsPage() {
  const [interests, setInterests] = useState<Interest[]>([]);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [subject, setSubject] = useState<Subject | null>(null);
  const [isFormOpen, setFormOpen] = useState(false);
  const [removing, setRemoving] = useState<Interest | null>(null);
  const [isRemoveOpen, setRemoveOpen] = useState(false);

  function openForm(next: Subject) {
    setSubject(next);
    setFormOpen(true);
  }

  function openRemove(interest: Interest) {
    setRemoving(interest);
    setRemoveOpen(true);
  }

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const list = await listInterests();
        if (cancelled) return;
        setInterests(list);
        setLoadError(null);
      } catch (caught) {
        if (!cancelled) setLoadError(caught);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    void load();

    return () => {
      cancelled = true;
    };
  }, []);

  /**
   * Alphabetical by label, which is what the API sorts by.
   *
   * There is no display order to match here, and deliberately no column for
   * one: these are tags in a picker rather than a list offered in an order.
   */
  function sorted(list: Interest[]): Interest[] {
    return [...list].sort((a, b) => a.label.localeCompare(b.label));
  }

  function onSaved(saved: Interest, mode: Subject['mode']) {
    setInterests((current) =>
      sorted(
        mode === 'edit'
          ? current.map((item) => (item.id === saved.id ? saved : item))
          : [...current, saved],
      ),
    );
    notifications.show({
      color: 'brand',
      message:
        mode === 'edit' ? `${saved.label} updated.` : `${saved.label} added.`,
    });
  }

  function onRemoved(removed: Interest) {
    setInterests((current) => current.filter((item) => item.id !== removed.id));
    notifications.show({ color: 'gray', message: `${removed.label} removed.` });
  }

  const header = (
    <PageHeader
      title="Interests"
      description="The subjects headlines are tagged with. This is what the platform learns about somebody."
      actions={
        <Button
          leftSection={<IconPlus size={16} />}
          onClick={() => openForm({ mode: 'create' })}
          disabled={isLoading || loadError !== null}
        >
          Add interest
        </Button>
      }
    />
  );

  if (isLoading) {
    return (
      <Stack gap="lg" className={classes.page}>
        {header}
        <Center py="xl">
          <Loader size="sm" />
        </Center>
      </Stack>
    );
  }

  if (loadError !== null) {
    return (
      <Stack gap="lg" className={classes.page}>
        {header}
        <Alert
          color="red"
          title="Cannot load the interests"
          icon={<IconAlertTriangle size={18} />}
        >
          <Text size="sm">{describeError(loadError)}</Text>
        </Alert>
      </Stack>
    );
  }

  return (
    <Stack gap="lg" className={classes.page}>
      {header}

      {interests.length === 0 ? (
        <Card>
          <Stack gap="xs" align="center" py="xl">
            <IconTags size={28} stroke={1.4} />
            <Text fw={600}>No interests yet</Text>
            <Text size="sm" c="dimmed" ta="center" maw={420}>
              Until there is at least one, every headline is untagged and the
              first run learns nothing from what people pick.
            </Text>
          </Stack>
        </Card>
      ) : (
        <Card p={0}>
          <Table.ScrollContainer minWidth={420}>
            <Table verticalSpacing="sm" horizontalSpacing="lg">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Interest</Table.Th>
                  <Table.Th w={160}>Code</Table.Th>
                  <Table.Th w={90} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {interests.map((interest) => (
                  <Table.Tr key={interest.id}>
                    <Table.Td>
                      <Text size="sm" fw={600}>
                        {interest.label}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge
                        variant="light"
                        color="gray"
                        className={classes.code}
                      >
                        {interest.code.toUpperCase()}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4} justify="flex-end" wrap="nowrap">
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          aria-label={`Edit ${interest.label}`}
                          onClick={() => openForm({ mode: 'edit', interest })}
                        >
                          <IconPencil size={16} stroke={1.7} />
                        </ActionIcon>
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          aria-label={`Remove ${interest.label}`}
                          onClick={() => openRemove(interest)}
                        >
                          <IconTrash size={16} stroke={1.7} />
                        </ActionIcon>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Card>
      )}

      <Text size="xs" c="dimmed">
        Nobody learning a language ever sees this list. The first run shows
        headlines and asks which somebody would actually click &mdash;
        categories collect what people think they should be interested in,
        headlines collect what they are. These tags are how that answer is read,
        so a headline tagged wrongly here produces a confidently wrong picture
        of somebody, and this portal is the only place that can be caught.
      </Text>

      <Modal
        opened={isFormOpen}
        onClose={() => setFormOpen(false)}
        centered
        title={
          subject?.mode === 'edit'
            ? `Edit ${subject.interest.label}`
            : 'Add an interest'
        }
      >
        {subject && (
          <InterestForm
            key={subject.mode === 'edit' ? subject.interest.id : 'create'}
            subject={subject}
            onClose={() => setFormOpen(false)}
            onSaved={onSaved}
          />
        )}
      </Modal>

      <Modal
        opened={isRemoveOpen}
        onClose={() => setRemoveOpen(false)}
        centered
        title={`Remove ${removing?.label ?? 'interest'}?`}
      >
        {removing && (
          <RemoveForm
            key={removing.id}
            interest={removing}
            onClose={() => setRemoveOpen(false)}
            onRemoved={onRemoved}
          />
        )}
      </Modal>
    </Stack>
  );
}
